// /admin/:slug — admin home: pick a date, see bookings + doc status,
// drill into a pilot.

import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Section, Cell, List, Caption, Spinner, Banner, Title, Subheadline, Button,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";
import BookingCalendar from "../components/BookingCalendar.jsx";
import { todayInMskYmd, endOfCalendarYmd } from "../lib/calendar.js";

const RU_WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const RU_MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

export default function AdminDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  usePageBack("/");
  const [search, setSearch] = useSearchParams();
  const date = search.get("date") || todayInMskYmd();
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);
  const [pilotCounts, setPilotCounts] = useState(null);
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    setBookings(null);
    api(`/api/v1/admin/airfields/${slug}/day?date=${date}`)
      .then((r) => setBookings(r.bookings || []))
      .catch((e) => setError(`Не удалось загрузить день: ${e.message}`));
  }, [slug, date]);

  // Pull just the counts (status=all returns the bucket totals) so the
  // dashboard can show a badge when pilots are waiting on verification.
  useEffect(() => {
    api(`/api/v1/admin/airfields/${slug}/pilots?status=all`)
      .then((r) => setPilotCounts(r.counts || null))
      .catch(() => {});
  }, [slug]);

  // 4-week overview for the calendar picker. Anchored to MSK today so
  // admins flying outside Russia don't see yesterday in the grid; the
  // backend mirrors the same anchor.
  useEffect(() => {
    const today = todayInMskYmd();
    const to = endOfCalendarYmd(today);
    api(`/api/v1/admin/airfields/${slug}/calendar?from=${today}&to=${to}`)
      .then((r) => {
        const map = {};
        for (const d of r.days || []) map[d.date] = d;
        setCalendar(map);
      })
      .catch(() => {});
  }, [slug]);

  return (
    <List>
      <Section>
        <Cell multiline subtitle={`Аэродром · ${slug}`}>
          <Title level="3">🛠 Управление · {formatDateRu(date)}</Title>
        </Cell>
      </Section>

      <Section header="Перейти к дате">
        <BookingCalendar
          daysByDate={calendar || {}}
          mode="admin"
          selectedYmd={date}
          onPick={(ymd) => setSearch({ date: ymd })}
        />
      </Section>

      <Section header="Управление">
        <Cell
          onClick={() => navigate(`/admin/${slug}/pilots`)}
          subtitle={pilotsSubtitle(pilotCounts)}
          after={pilotsBadge(pilotCounts)}
          multiline
        >
          👥 Пилоты аэродрома
        </Cell>
        <Cell
          onClick={() => navigate(`/admin/${slug}/day?date=${date}`)}
          subtitle="Открыто / с ограничениями / закрыто + заметка"
          after="→"
        >
          🛬 Статус дня
        </Cell>
        <Cell
          onClick={() => navigate(`/admin/${slug}/announce`)}
          subtitle="Разослать текст всем активным членам аэродрома"
          after="→"
        >
          📣 Объявление
        </Cell>
      </Section>

      <AirfieldPhotoCard slug={slug} />


      {error && <Banner type="section" header="Ошибка">{error}</Banner>}
      {bookings === null && !error && (
        <Section><Cell before={<Spinner size="m" />}>Загружаем брони…</Cell></Section>
      )}

      {bookings !== null && (
        <Section header={`Брони (${bookings.length})`}>
          {bookings.length === 0 && (
            <Cell subtitle="На этот день никто не записан.">Тишина</Cell>
          )}
          {bookings.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              onClick={() => navigate(`/admin/${slug}/pilot/${b.user_id}?from=${encodeURIComponent(date)}`)}
            />
          ))}
        </Section>
      )}

      <Section>
        <Cell>
          <Caption level="1">
            Тап по строке открывает анкету пилота: документы, статус подтверждения, действия.
            Закрытие дня и анонсы — отдельный экран (RCF-023).
          </Caption>
        </Cell>
        <Cell onClick={() => navigate("/")}>← На главную</Cell>
      </Section>
    </List>
  );
}

// Hero photo for the airfield, surfaced on Home as the admin's avatar.
// Reads /api/v1/me to find the airfield's current photo_url, lets the admin
// upload a JPEG/PNG/WebP, and clears it on demand. Errors render inline.
function AirfieldPhotoCard({ slug }) {
  const fileRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api("/api/v1/me").then((r) => {
      if (cancelled) return;
      const af = r.admin_airfields?.find((x) => x.slug === slug);
      setPhotoUrl(af?.photo_url || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setErr("Поддерживаются JPEG, PNG, WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Файл больше 5 МБ.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const base64 = await readAsBase64(file);
      const r = await api(`/api/v1/admin/airfields/${slug}/photo`, {
        method: "POST",
        body: JSON.stringify({ content_type: file.type, base64 }),
      });
      setPhotoUrl(r.photo_url);
    } catch (e2) {
      setErr(`Не удалось загрузить: ${e2.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onClear() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/v1/admin/airfields/${slug}/photo`, { method: "DELETE" });
      setPhotoUrl(null);
    } catch (e2) {
      setErr(`Не удалось удалить: ${e2.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section header="Фото аэродрома">
      <Cell
        before={
          photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              style={{
                width: 56, height: 56, borderRadius: 12, objectFit: "cover",
                background: "var(--tgui--secondary_bg_color, #eee)",
              }}
            />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 12,
              background: "var(--tgui--secondary_bg_color, #eee)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24,
            }}>🛫</div>
          )
        }
        subtitle="Появится на главной как аватар админа"
        multiline
      >
        {photoUrl ? "Фото загружено" : "Фото не задано"}
      </Cell>
      <Cell>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            size="s"
            mode="filled"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {photoUrl ? "Заменить" : "Загрузить"}
          </Button>
          {photoUrl && (
            <Button size="s" mode="plain" disabled={busy} onClick={onClear}>
              Удалить
            </Button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={onPick}
          />
        </div>
      </Cell>
      {err && <Cell multiline><Caption level="1" style={{ color: "#c00" }}>{err}</Caption></Cell>}
    </Section>
  );
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || "");
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    r.onerror = () => reject(new Error("Не удалось прочитать файл."));
    r.readAsDataURL(file);
  });
}

function BookingRow({ booking, onClick }) {
  const start = new Date(booking.start_at);
  const end = new Date(booking.end_at);
  const time = `${formatHHMM(start)}–${formatHHMM(end)}`;
  const name =
    [booking.last_name, booking.first_name, booking.middle_name].filter(Boolean).join(" ") ||
    `tg:${booking.tg_user_id}`;
  const verified = booking.verification_status === "verified";
  const docChips = (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
      {chip("ID", booking.fasr_ok)}
      {chip("Страх.", booking.ins_ok)}
      {chip("Подтв.", verified)}
    </div>
  );
  return (
    <Cell
      onClick={onClick}
      subtitle={`${time} · ${booking.class_code}`}
      after="→"
      multiline
    >
      <span style={{ fontWeight: 600 }}>{name}</span>
      {docChips}
    </Cell>
  );
}

function chip(label, ok) {
  return (
    <span className={`rcf-chip rcf-chip--${ok ? "ok" : "miss"}`}>
      {ok ? "✓" : "·"} {label}
    </span>
  );
}

function pilotsSubtitle(counts) {
  if (!counts) return "Список членов, верификация, приём новых";
  const total = counts.all ?? 0;
  const pending = (counts.pending ?? 0) + (counts.unverified ?? 0);
  if (pending > 0) return `${total} пилот${pluralRu(total, "", "а", "ов")} · ${pending} ждут проверки`;
  return `${total} пилот${pluralRu(total, "", "а", "ов")} · все проверены`;
}

function pilotsBadge(counts) {
  if (!counts) return "→";
  const pending = (counts.pending ?? 0) + (counts.unverified ?? 0);
  if (pending > 0) return <span className="rcf-pill rcf-pill--warn">{pending}</span>;
  return "→";
}

function pluralRu(n, one, few, many) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function formatDateRu(ymd) {
  const d = parseYmd(ymd);
  return `${RU_WEEKDAYS[d.getDay()]} ${d.getDate()} ${RU_MONTHS[d.getMonth()]}`;
}
function formatHHMM(d) {
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}
